// ── Enum label maps ────────────────────────────────────────────────────

export const CONSTRAINT_TYPE_LABELS: Record<string, string> = {
  team_blackout: "Team unavailable window",
  must_play_before: "Must finish by",
  must_play_after: "Cannot start until",
  coach_conflict: "Shared coach conflict",
  venue_preference: "Venue preference",
  game_count_limit: "Game count limit",
  divisional_ordering: "Divisional ordering",
  do_not_play: "Do not play specific team",
  must_play_same_time: "Must play same time as team",

};

export const SEVERITY_LABELS: Record<string, string> = {
  hard: "Hard",
  soft: "Soft",
  preference: "Preference",
};

export const STATUS_LABELS: Record<string, string> = {
  pending_review: "Pending review",
  approved: "Approved",
  partial: "Partial",
  denied: "Denied",
  superseded: "Superseded",
};

export const SOURCE_LABELS: Record<string, string> = {
  coach_request: "Coach request",
  operator: "Operator",
  system: "System",
};

// ── Formatting helpers ─────────────────────────────────────────────────

import { formatTime } from "@/lib/dates";

/** All clock times render through the shared 12-hour helper. */
const formatTime12 = (time24: string): string => formatTime(time24, "?");

function formatDateShort(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ── Humanize ───────────────────────────────────────────────────────────

interface HumanizeOptions {
  opponentNameLookup?: (teamId: string) => string | undefined;
}

interface ConstraintRow {
  constraint_type: string;
  parameters: Record<string, any>;
}

export function humanizeConstraint(
  constraint: ConstraintRow,
  options: HumanizeOptions = {}
): string {
  const { constraint_type: type, parameters: params } = constraint;
  const { opponentNameLookup } = options;

  if (type === "team_blackout") {
    const date = params.date ? formatDateShort(params.date) : "?";
    const start = params.start_time ? formatTime12(params.start_time) : "?";
    const end = params.end_time ? formatTime12(params.end_time) : "?";
    return `Unavailable on ${date} from ${start} to ${end}`;
  }

  if (type === "must_play_before") {
    const date = params.date ? formatDateShort(params.date) : "?";
    const time = params.time ? formatTime12(params.time) : "?";
    return `Last game must end by ${date} at ${time}`;
  }

  if (type === "must_play_after") {
    const date = params.date ? formatDateShort(params.date) : "?";
    const time = params.time ? formatTime12(params.time) : "?";
    return `First game must start after ${date} at ${time}`;
  }

  if (type === "coach_conflict") {
    const name =
      opponentNameLookup?.(params.related_team_id) ?? "another team";
    return `Cannot play at the same time as ${name}`;
  }

  const label = CONSTRAINT_TYPE_LABELS[type] || type;
  return `${label} (see details)`;
}
